import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ApiError } from "@/lib/api";
import {
  MACOS_OPS,
  macosHostView,
  macosOpBody,
  type MacosBoxLike,
  type MacosOpKey,
  type Tone,
} from "@/lib/macosHosts";

/**
 * The Fleet page row of the macOS fleet host (plans/2026-10-15-macos-fleet-host.md 9.1): state, release,
 * battery/AC, clock health, power-settings drift, thermals, the Mac's own queue depth, last job, LAN address / peer firewall, and
 * the operations an operator has without a shell: drain / undrain, restart the service, update, plus the
 * service's read-only probes. All state comes from the Mac's own heartbeat as sanitized by the broker
 * (lib/macosHosts.ts); the broker's allowlists decide what an op may do (private/auth macosFleet.ts).
 */
type Call = <T>(path: string, init?: RequestInit) => Promise<T>;

export interface MacosPanelBox extends MacosBoxLike {
  box_id: string;
  hostname: string;
}

const TONE_CLASS: Record<Tone, string> = {
  ok: "text-green-700 dark:text-green-400",
  info: "text-foreground",
  warn: "text-yellow-700 dark:text-yellow-400",
  bad: "text-destructive",
  muted: "text-muted-foreground",
};

const TONE_BADGE: Record<
  Tone,
  "success" | "info" | "warning" | "destructive" | "secondary"
> = {
  ok: "success",
  info: "info",
  warn: "warning",
  bad: "destructive",
  muted: "secondary",
};

const PROBES = [
  { op: "preflight", label: "Preflight", args: {} },
  { op: "toolchain-info", label: "Toolchain", args: {} },
  { op: "seatbelt-probe", label: "Sandbox probe", args: {} },
  { op: "pf-status", label: "pf status", args: {} },
  {
    op: "diagnose-unit",
    label: "Service log",
    args: { unit: "dev.pure.macos-tester", lines: 120 },
  },
  { op: "read-inventory", label: "Inventory", args: {} },
  { op: "read-reconcile-log", label: "Reconcile log", args: { lines: 200 } },
] as const;

function errText(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

export function MacosHostPanel({
  call,
  box,
  onChanged,
}: {
  call: Call;
  box: MacosPanelBox;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(
    null,
  );
  const [probe, setProbe] = useState<{ op: string; payload: unknown } | null>(
    null,
  );

  const view = macosHostView(box, Date.now());

  async function runOp(key: MacosOpKey) {
    const def = MACOS_OPS[key];
    if (def.confirm && !window.confirm(def.confirm)) return;
    setBusy(key);
    setMessage(null);
    try {
      const r = await call<{
        ok: boolean;
        reason: string | null;
        relayed: boolean;
      }>("/fleet/macos/op", {
        method: "POST",
        body: JSON.stringify(macosOpBody(box.box_id, key)),
      });
      setMessage({
        ok: r.ok,
        text: r.ok
          ? r.relayed
            ? `${def.op}: done`
            : `${def.op}: recorded (the Mac did not answer: ${r.reason ?? "unreachable"}; it applies when it is back)`
          : `${def.op}: ${r.reason ?? "failed"}`,
      });
      onChanged();
    } catch (err) {
      setMessage({ ok: false, text: errText(err, `${def.op} failed`) });
    } finally {
      setBusy(null);
    }
  }

  async function runProbe(p: (typeof PROBES)[number]) {
    setBusy(p.op);
    setMessage(null);
    try {
      const r = await call<{
        ok: boolean;
        payload: unknown;
        reason: string | null;
      }>("/fleet/diagnose", {
        method: "POST",
        body: JSON.stringify({ box_id: box.box_id, op: p.op, args: p.args }),
      });
      if (r.ok) setProbe({ op: p.op, payload: r.payload });
      else setMessage({ ok: false, text: `${p.op}: ${r.reason ?? "failed"}` });
    } catch (err) {
      setMessage({ ok: false, text: errText(err, `${p.op} failed`) });
    } finally {
      setBusy(null);
    }
  }

  const offline = view.phase === "offline";
  return (
    <section
      aria-label={`macOS host ${box.hostname}`}
      className="mb-3 space-y-3 rounded-md border p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={TONE_BADGE[view.tone]}>{view.label}</Badge>
        <span className="text-xs text-muted-foreground">macOS host</span>
      </div>
      {view.reasons.length > 0 && (
        <ul
          role="status"
          aria-live="polite"
          className={cn("list-disc pl-5 text-xs", TONE_CLASS[view.tone])}
        >
          {view.reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
        {view.facts.map((f) => (
          <div key={f.label} className="flex gap-2">
            <dt className="w-28 shrink-0 text-muted-foreground">{f.label}</dt>
            <dd className={cn("min-w-0 break-words", TONE_CLASS[f.tone])}>
              {f.value}
            </dd>
          </div>
        ))}
      </dl>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant={view.drained ? "default" : "outline"}
          disabled={busy !== null}
          onClick={() => runOp(view.drainAction)}
        >
          {busy === view.drainAction ? "…" : view.drained ? "Undrain" : "Drain"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy !== null || offline}
          onClick={() => runOp("restart")}
        >
          Restart service
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy !== null || offline}
          title="pure-deploy update macos-tester: verifies the signed release, waits for the Mac to be idle, rolls back on a failed health check"
          onClick={() => runOp("update")}
        >
          Update now
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy !== null || offline}
          onClick={() => runOp("purge")}
        >
          Purge job dirs
        </Button>
        <span className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
        {PROBES.map((p) => (
          <Button
            key={p.op}
            size="sm"
            variant="ghost"
            disabled={busy !== null || offline}
            onClick={() => runProbe(p)}
          >
            {busy === p.op ? "…" : p.label}
          </Button>
        ))}
      </div>
      {message && (
        <p
          role={message.ok ? "status" : "alert"}
          className={cn(
            "text-xs",
            message.ok ? "text-muted-foreground" : "text-destructive",
          )}
        >
          {message.text}
        </p>
      )}
      {probe && (
        <details open>
          <summary className="cursor-pointer text-xs text-muted-foreground">
            {probe.op} result
          </summary>
          <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 font-mono text-[10px]">
            {typeof probe.payload === "string"
              ? probe.payload
              : JSON.stringify(probe.payload, null, 2)}
          </pre>
        </details>
      )}
    </section>
  );
}
